import 'components/ui/dropdowns/custom-dropdown/index.css'
import 'components/ui/dropdowns/react-select-v5-theme.css'

import cn from 'clsx'
import * as React from 'react'
import { components as selectComponents } from 'react-select'
import type { DropdownIndicatorProps, MultiValueProps } from 'react-select'
import AsyncCreatableSelect from 'react-select/async-creatable'
import InlineSVG from 'svg-inline-react'

import type { DropdownOption } from 'types/ui'

import { matchesExistingOption } from 'components/ui/dropdowns/match-existing-option'

type Props = {
  onChange: (...args: Array<any>) => any
  value: (Record<string, any> | Record<string, any>[]) | null | undefined
  name?: string
  options: DropdownOption[]
  promptTextCreator?: (...args: Array<any>) => any
  valueComponent: React.ElementType
  optionRenderer?: (...args: Array<any>) => any
  className?: string
  newOptionCreator?: (...args: Array<any>) => any
  multi?: boolean
  clearable?: boolean
  placeholder?: string
  isValidNewOption: (option: DropdownOption) => boolean
  hideArrow?: boolean
  simpleValue?: boolean
  loadOptions: (...args: Array<any>) => any
  disabled?: boolean
}

const IndicatorSeparator = () => null

const noOptionsMessage = ({ inputValue }: { inputValue: string }) =>
  inputValue.trim() ? 'No results found' : 'Type to search'

/**
 * Adapter that maps the caller's older prop shape (callback-style `loadOptions`,
 * `valueComponent`, `newOptionCreator`) onto react-select v5.
 */
const RecipientDropdown = (props: Props): React.ReactNode => {
  const {
    name,
    value,
    options,
    isValidNewOption,
    multi,
    clearable,
    placeholder,
    hideArrow,
    onChange,
    promptTextCreator,
    valueComponent,
    optionRenderer,
    className,
    newOptionCreator,
    loadOptions,
    disabled,
  } = props

  // Callers pass a callback-style loader; wrap it in a promise for v5.
  const asyncLoadOptions = (inputValue: string) =>
    new Promise<DropdownOption[]>(resolve => {
      loadOptions(inputValue, (_err: unknown, data: any) => {
        resolve(Array.isArray(data) ? data : data?.options ?? [])
      })
    })

  const DropdownIndicator = React.useCallback(
    (indicatorProps: DropdownIndicatorProps<DropdownOption, boolean>) =>
      hideArrow ? null : (
        // Cast: react-select's component types widen Option when isMulti is dynamic.
        <selectComponents.DropdownIndicator {...(indicatorProps as any)}>
          <InlineSVG
            src={require('!svg-inline!assets/icons/arrow-down.svg')}
            width="24"
            height="24"
            raw
          />
        </selectComponents.DropdownIndicator>
      ),
    [hideArrow],
  )

  const MultiValue = React.useCallback(
    (multiValueProps: MultiValueProps<DropdownOption, boolean>) => {
      const ValueComponent = valueComponent
      return <ValueComponent value={multiValueProps.data} />
    },
    [valueComponent],
  )

  const components = React.useMemo(
    () => ({
      DropdownIndicator,
      IndicatorSeparator,
      ...(multi ? { MultiValue } : {}),
    }),
    [DropdownIndicator, MultiValue, multi],
  )

  return (
    <AsyncCreatableSelect<DropdownOption, boolean>
      classNamePrefix="Select"
      name={name}
      className={cn('Select', className)}
      placeholder={placeholder}
      isMulti={multi}
      isClearable={!!clearable}
      isDisabled={disabled}
      value={value as any}
      // Show the caller's suggestions before any search.
      defaultOptions={options}
      loadOptions={asyncLoadOptions}
      noOptionsMessage={noOptionsMessage}
      onChange={newValue => onChange(newValue)}
      // Server already filters; keep every loaded option.
      filterOption={null}
      formatCreateLabel={input =>
        promptTextCreator ? promptTextCreator(input) : `Create "${input}"`
      }
      getNewOptionData={(inputValue, optionLabel) =>
        (newOptionCreator
          ? newOptionCreator({
              label: inputValue,
              labelKey: 'label',
              valueKey: 'value',
            })
          : { label: optionLabel, value: inputValue }) as DropdownOption
      }
      isValidNewOption={(inputValue, selectValue, selectOptions) =>
        inputValue.trim().length > 0 &&
        isValidNewOption({
          label: inputValue,
          labelKey: 'label',
          valueKey: 'value',
        } as any) &&
        // Overriding isValidNewOption disables v5's built-in uniqueness check;
        // re-apply it so an existing recipient can't be offered as "Create".
        !matchesExistingOption(inputValue, selectValue, selectOptions)
      }
      formatOptionLabel={(option: any, { context }) =>
        context === 'value' || option.__isNew__ || !optionRenderer
          ? option.label
          : optionRenderer(option)
      }
      components={components}
    />
  )
}

export default RecipientDropdown
